import type {SingleTaskBatchScanResult} from "../../scan-model/GroupedTaskBatchesScanResult.tsx";

export interface BatchOverlapResult {
    meanOverlappingBatches: number
    maxOverlappingBatches: number

    /**
     * These are the means of the number of tasks in each batch for the batches that overlap, based on the mean/max of the individual batches
     */
    meanOverlappingMeanTasks: number
    meanOverlappingMaxTasks: number

    /**
     * These are the highest value we get when summing the number of tasks in each batch for the batches that overlap,
     * based on the mean/max of the individual batches.
     */
    maxOverlappingMeanTasks: number
    maxOverlappingMaxTasks: number
}

export function calculateBatchOverlaps(batches: SingleTaskBatchScanResult[]): BatchOverlapResult {
    if (batches.length === 0) {
        return {
            meanOverlappingBatches: 0,
            maxOverlappingBatches: 0,
            meanOverlappingMeanTasks: 0,
            meanOverlappingMaxTasks: 0,
            maxOverlappingMeanTasks: 0,
            maxOverlappingMaxTasks: 0
        };
    }

    // Create events for batch starts and ends
    interface BatchTimeEvent {
        time: number;
        type: 'start' | 'end';
        batch: SingleTaskBatchScanResult;
    }

    const events: BatchTimeEvent[] = [];
    
    // Add start and end events for each batch
    batches.forEach(batch => {
        events.push({ time: batch.overallStartTime, type: 'start', batch });
        events.push({ time: batch.overallEndTime, type: 'end', batch });
    });

    // Sort events by time, with 'start' events before 'end' events at the same time
    events.sort((a, b) => {
        if (a.time !== b.time) {
            return a.time - b.time;
        }
        // If times are equal, process 'start' events before 'end' events
        return a.type === 'start' ? -1 : 1;
    });

    const currentOverlappingBatches: SingleTaskBatchScanResult[] = [];
    let maxOverlappingBatches = 0;
    let totalOverlapTime = 0;
    let totalTime = 0;
    let lastTime = events[0]?.time || 0;

    // Track overlap statistics over time
    let totalOverlappingMeanTasksTime = 0;
    let totalOverlappingMaxTasksTime = 0;
    let maxOverlappingMeanTasks = 0;
    let maxOverlappingMaxTasks = 0;

    // Process events to calculate overlaps
    for (const event of events) {
        // Calculate time interval and update statistics
        const timeDiff = event.time - lastTime;
        if (timeDiff > 0 && currentOverlappingBatches.length > 0) {
            const currentOverlapCount = currentOverlappingBatches.length;
            
            // Calculate mean and max tasks for currently overlapping batches
            const meanTasks = currentOverlappingBatches.reduce((sum, batch) => 
                sum + batch.overlaps.meanOverlap, 0) / currentOverlapCount;
            const maxTasks = Math.max(...currentOverlappingBatches.map(batch => 
                batch.overlaps.highestOverlap));
            
            // Sum of mean and max tasks across all overlapping batches
            const sumMeanTasks = currentOverlappingBatches.reduce((sum, batch) => 
                sum + batch.overlaps.meanOverlap, 0);
            const sumMaxTasks = currentOverlappingBatches.reduce((sum, batch) => 
                sum + batch.overlaps.highestOverlap, 0);

            // Weight by time for mean calculations
            totalOverlapTime += currentOverlapCount * timeDiff;
            totalOverlappingMeanTasksTime += meanTasks * timeDiff;
            totalOverlappingMaxTasksTime += maxTasks * timeDiff;
            
            // Track maximums
            maxOverlappingMeanTasks = Math.max(maxOverlappingMeanTasks, sumMeanTasks);
            maxOverlappingMaxTasks = Math.max(maxOverlappingMaxTasks, sumMaxTasks);

            // We have this in here to discard empty time, because we want to scale for user's busy periods. Not useful to scale based on nothing running overnight
            //   when they hammer the instance in the day for example.
            totalTime += timeDiff;
        }

        // Update current overlapping batches
        if (event.type === 'start') {
            currentOverlappingBatches.push(event.batch);
            maxOverlappingBatches = Math.max(maxOverlappingBatches, currentOverlappingBatches.length);
        } else {
            const index = currentOverlappingBatches.findIndex(batch => batch === event.batch);
            if (index !== -1) {
                currentOverlappingBatches.splice(index, 1);
            }
        }

        lastTime = event.time;
    }

    // Calculate final means
    const meanOverlappingBatches = totalTime > 0 ? totalOverlapTime / totalTime : 0;
    const meanOverlappingMeanTasks = totalTime > 0 ? totalOverlappingMeanTasksTime / totalTime : 0;
    const meanOverlappingMaxTasks = totalTime > 0 ? totalOverlappingMaxTasksTime / totalTime : 0;

    return {
        meanOverlappingBatches,
        maxOverlappingBatches,
        meanOverlappingMeanTasks,
        meanOverlappingMaxTasks,
        maxOverlappingMeanTasks,
        maxOverlappingMaxTasks
    };
}